import { useState, useRef, useEffect } from 'react';
import type { View } from './views';
import { isStandaloneMobile } from '../lib/nativeBridge';
import { NAV_VIEW_IDS } from '../components/ui/NavMenuItems';

/**
 * Navigation hook — owns the hand-rolled `view` state-machine plus the
 * browser-history (hash) sync and the help-routing atoms. Lifted verbatim from
 * `Padel24AppMain` (extracted from the former monolith; see
 * docs/history/DECOMPOSITION_PLAN.md §3) as the first step of the `AppShell` shrink.
 *
 * Behaviour-preserving: the two history effects, the `popInProgress` guard, and
 * `openHelp` are byte-for-byte the original logic — only TypeScript types were
 * added. The effects only ever read/write `window.history`, `view`, and the
 * `popInProgress` ref, so they do not interact with the other mount effects in
 * `Padel24AppMain`; calling this hook at the top of the component is therefore
 * order-independent vs. those effects.
 */
// ── TWO ADDRESSES FOR ONE VIEW (#847 review) ────────────────────────
//
// The `mobile` view is shown on two different SURFACES, and the address must
// say which, because the address is what a reload and the history replay:
//
//   `#mobile`          the PLAYER APP itself — a phone browser, a desktop
//                      browser, the native shell (which adds `?native=1`)
//   `#mobile-preview`  the portal's «Mobilapp» PREVIEW: an operator looking at
//                      the player app from inside the portal, in a phone frame
//
// They shared `#mobile` until this. `useNavigation` serialised every view as
// its own name, so an operator opening the preview wrote `#mobile` — and the
// predicate below, reading that same hash, then called the page the player
// surface: a reload turned the operator's preview into the player entrance,
// and the per-render login decision disagreed with the portal that was
// mounted. Surface identity and «which view the portal is showing» were one
// string; now they are two.
export const MEMBER_HASH = '#mobile';
export const PREVIEW_HASH = '#mobile-preview';

/**
 * Pure: the view to start on. `#mobile` in the URL (or the native shell's
 * `?native=1`) opens the member app directly — that is how the Expo shell
 * (apps/mobile) and a phone browser reach it — and `#mobile-preview` reopens
 * the portal's preview of it. Every other hash still starts at «home»: those
 * views need state the app builds up on the way there.
 */
export function initialViewFromLocation(
  hash: string = typeof window === 'undefined' ? '' : window.location.hash,
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): View {
  if (hash === MEMBER_HASH || hash === PREVIEW_HASH || isStandaloneMobile(search)) return 'mobile';
  // A VIEW THE MENU OPENS DIRECTLY is restored from its own hash (#1145, AUD-030):
  // Banebooking, Aktiviteter, Spillere and the rest need nothing but a click from
  // Hjem, so a reload or a bookmark lands on them. Reception lost Banebooking on
  // every refresh. Every other view still starts at «home», because it needs
  // state built up on the way.
  const named = hash.startsWith('#') ? hash.slice(1) : '';
  if (named && named !== 'mobile' && NAV_VIEW_IDS.includes(named)) return named as View;
  return 'home';
}

/** The address a view is written to, on the surface it is shown on. */
export function hashForView(view: View, memberSurface: boolean): string {
  if (view === 'mobile') return memberSurface ? MEMBER_HASH : PREVIEW_HASH;
  return `#${view}`;
}

/**
 * Is this page the MEMBER SURFACE — the player app?
 *
 * ONE ANSWER, ASKED IN THREE PLACES: the portal login gate and the member gate
 * in `Padel24App`, and the chrome and layout in `AppShell`. It is a question
 * about which SURFACE the page is, and the native shell (`?native=1`) is only
 * one of the two ways to arrive at it — a plain browser at `/#mobile` is the
 * other.
 *
 * The three asked different questions until now. The gates and the layout used
 * `isStandaloneMobile()` — «is this the native shell?» — and the difference only
 * showed in a browser: a player who signed in and opened their own club was
 * shown the club portal's LOGIN, and had they got past it, the player app would
 * have rendered inside a development phone frame with the portal's header on
 * top. Natively all three predicates agree, which is why the app never showed
 * it. `isStandaloneMobile()` keeps the one job that really is about the shell:
 * withholding the organization door, because the native app has no way back
 * from the portal.
 */
export function isMemberSurfaceLocation(
  hash: string = typeof window === 'undefined' ? '' : window.location.hash,
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): boolean {
  // NOT «the view is `mobile`»: the portal's preview shows that view too, at
  // `#mobile-preview`, and is the portal surface.
  return hash === MEMBER_HASH || isStandaloneMobile(search);
}

export function useNavigation(
  initialView: View = 'home',
  /**
   * Whether this shell IS the player app. Decides only how the `mobile` view
   * is written to the address — see `hashForView`. Read once by the caller,
   * like the shell's own `standaloneMobile`.
   */
  memberSurface = false,
) {
  const [view, setView] = useState<View>(initialView);
  // Help view: remembers where the user came from so "Back" in help routes correctly
  const [helpReturnView, setHelpReturnView] = useState<View>('home');
  const [helpInitialFormat, setHelpInitialFormat] = useState('padel24');

  // ── BROWSER HISTORY ─────────────────────────────────────────
  //
  // Uses the hash segment (#view) as the "URL" — the app is an SPA without
  // server-side routing, so we don't want real pathnames.
  const popInProgress = useRef(false);

  useEffect(() => {
    // Initialize the history state on first mount so that
    // popstate events have something to fall back on.
    if (!window.history.state || !window.history.state.padel24View) {
      window.history.replaceState({ padel24View: view }, '', hashForView(view, memberSurface));
    }

    const onPop = (e: PopStateEvent) => {
      const targetView = (e.state?.padel24View || 'home') as View;
      popInProgress.current = true;
      setView(targetView);
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip the push if the change came from popstate (we are already on the
    // correct history entry — pushing would create a duplicate entry).
    // On popstate we also keep the scroll position, since the user
    // expects to end up where they last were.
    if (popInProgress.current) {
      popInProgress.current = false;
      return;
    }
    // Skip if the current state already matches (prevents dupes on
    // initial mount and from redundant setView calls).
    if (window.history.state?.padel24View === view) {
      return;
    }
    window.history.pushState({ padel24View: view }, '', hashForView(view, memberSurface));

    // Scroll to the top on FORWARD navigation (clicking links/buttons).
    // We do NOT do this on popstate because the user expects to keep the
    // position on browser-back (that's the convention in all browsers).
    //
    // requestAnimationFrame ensures the new content is rendered before we scroll,
    // otherwise the scroll gets "eaten" by the layout shift when the new page is drawn.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
    // `memberSurface` is fixed for the shell's life (read once by the caller);
    // listed because the address it chooses depends on it.
  }, [view, memberSurface]);

  // Open the help page for a given format, with the back-route set
  const openHelp = (format = 'padel24', returnTo: View = view) => {
    setHelpInitialFormat(format);
    setHelpReturnView(returnTo);
    setView('help');
  };

  return { view, setView, helpReturnView, helpInitialFormat, openHelp };
}

/**
 * The shell's SURFACE and its navigation, decided together (#847 review).
 *
 * `AppShell` calls exactly this, and so does the route test's probe shell —
 * so the test runs the shell's own surface decision rather than a copy of
 * it, which is how a probe certifies a shell that no longer exists.
 *
 * The surface is read ONCE, at mount: the shell does not change surface under
 * a running page (a surface change remounts it — `Padel24App` keys the store
 * on it). The view then moves freely, and is written to the address that
 * says which surface it is on.
 */
export function useShellNavigation() {
  const [memberSurface] = useState(() => isMemberSurfaceLocation());
  const nav = useNavigation(initialViewFromLocation(), memberSurface);
  return { memberSurface, ...nav };
}
